/**
 * @fileoverview State and submit flow for the event registration modal.
 * @module hooks/useEventRegistrationModal
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { getProfileByNumber } from "../utils/common.js";
import { useFieldValidation } from "./useFieldValidation";
import {
  isRegistrationIncomplete,
  loadRegistrationInfo,
  loadSectors,
  registerAttendee,
} from "../modals/EventRegistrationModal/eventRegistrationApi.js";

/**
 * Empty registration form. Every required value starts as null so the completeness check can see what is still missing.
 * `perfilSeleccionado` starts at 0, which means the root company profile rather than a row in `perfiles`.
 * `codiogPais` keeps the API's spelling.
 *
 * Opening the dialog clears the sector selects and refetches sectors.
 * A user who is not type "4" loads their existing attendee record and the fields stay disabled.
 * `user` is not an effect dependency: adding it would reload the form on unrelated parent renders.
 *
 * Completeness ignores expectations and commercial interests when the event does not require them.
 * The effect that computes it depends on `formData` only, matching the previous modal behavior.
 *
 * Changing the scope select updates the root field for profile 0.
 * For any other profile it updates that profile's row, and falls back to the root field when the row is missing.
 */
const INITIAL_FORM = {
  razonSocial: null,
  RFC: null,
  perfilSeleccionado: 0,
  sector: null,
  subSector: null,
  rama: null,
  numeroTotalTrabajadores: null,
  ambitoOperaciones: null,
  nombre: null,
  codiogPais: null,
  telefono: null,
  correoElectronico: null,
  puesto: null,
  area: null,
  genero: null,
  interesesComerciales: null,
  expectativas: null,
};

const useEventRegistrationModal = ({ open, onClose, event, user }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const selectSectorRef = useRef(null);
  const selectSubsectorRef = useRef(null);
  const selectBranchRef = useRef(null);
  const dialogRef = useRef(null);
  const {
    handleFieldChange,
    getErrorMessage,
    getFieldValidationState,
    hasErrors,
  } = useFieldValidation({
    nombre: "",
    telefono: "",
    correoElectronico: "",
    interesesComerciales: "",
    expectativas: "",
    companyRFC: "",
    numeroTotalTrabajadores: "",
  });

  const [sectors, setSectors] = useState([]);
  const [subSectors, setSubsectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showError, setShowError] = useState(false);
  const [isFormDisabled, setIsFormDisabled] = useState(true);
  const [isFormIncomplete, setIsFormIncomplete] = useState(true);
  const [formData, setFormData] = useState(INITIAL_FORM);

  /**
   * Posts the registration and turns field errors on when expectations or commercial interests are required but empty.
   * @returns {Promise<void>}
   */
  const handleRegister = useCallback(async () => {
    setShowError(false);
    const result = await registerAttendee({
      formData,
      event,
      navigate,
      onClose,
    });
    if (result?.invalid) {
      setShowError(true);
    }
  }, [formData, event, navigate, onClose]);

  useEffect(() => {
    const handleClose = () => {
      onClose();
    };

    const dialog = dialogRef.current;
    if (dialog) {
      dialog.addEventListener("close", handleClose);
    }

    if (open) {
      setShowError(false);
      setSectors([]);
      setSubsectors([]);
      setBranches([]);
      selectSectorRef.current?.clearValue();
      selectSubsectorRef.current?.clearValue();
      selectBranchRef.current?.clearValue();

      if (user && user?.tipoDeUsuario !== "4") {
        setIsFormDisabled(true);
        setIsFormIncomplete(false);
        loadRegistrationInfo(navigate).then((registration) => {
          if (registration != null) {
            setFormData(registration);
          }
        });
      } else {
        setIsFormDisabled(false);
        setIsFormIncomplete(true);
      }

      loadSectors(navigate).then((sectorList) => {
        if (sectorList != null) {
          setSectors(sectorList);
        }
      });
      dialog.showModal();
    } else {
      dialog.close();
    }

    return () => {
      if (dialog) {
        dialog.removeEventListener("close", handleClose);
      }
    };
    // user is omitted so a parent re-render does not reload the form mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onClose, navigate]);

  useEffect(() => {
    setIsFormIncomplete(isRegistrationIncomplete(formData, event));
  }, [formData]);

  /**
   * Writes one registration field.
   * @param {string} field - Form field name.
   * @param {unknown} value - New value.
   * @returns {void}
   */
  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Updates the operations scope from the scope select.
   * Profile 0 writes the root `ambitoOperaciones`. Any other profile writes that row in `perfiles`.
   * If that row is missing, the value falls back to the root field.
   * @param {{value: string}|null} selectedOption - Selected scope option. Ignored when cleared.
   * @returns {void}
   */
  const handleAmbitoOperacionesChange = useCallback(
    (selectedOption) => {
      if (!selectedOption) {
        return;
      }
      const selectedProfileIndex = formData.perfilSeleccionado ?? 0;
      if (selectedProfileIndex === 0) {
        handleChange("ambitoOperaciones", selectedOption.value);
        return;
      }
      const profileNumber = selectedProfileIndex;
      const profile = getProfileByNumber(formData, profileNumber);
      if (profile && formData.perfiles) {
        setFormData((prev) => ({
          ...prev,
          perfiles: prev.perfiles.map((profileRow) =>
            profileRow?.numeroDePerfil === profileNumber
              ? { ...profileRow, ambitoOperaciones: selectedOption.value }
              : profileRow,
          ),
        }));
        return;
      }
      handleChange("ambitoOperaciones", selectedOption.value);
    },
    [formData, handleChange],
  );

  return {
    t,
    open,
    onClose,
    dialogRef,
    formData,
    setFormData,
    isFormDisabled,
    sectors,
    subSectors,
    branches,
    selectSectorRef,
    selectSubsectorRef,
    selectBranchRef,
    setSubsectors,
    setBranches,
    showError,
    event,
    getErrorMessage,
    getFieldValidationState,
    handleFieldChange,
    handleChange,
    handleAmbitoOperacionesChange,
    handleRegister,
    hasErrors,
    isFormIncomplete,
  };
};

export default useEventRegistrationModal;
